import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { tagService, categoryService } from '../services/api';
import { Card, Button, Table, Pagination, useToast, StatusToggle, PageHeader, Select } from './common';
import { usePageTitle } from '../context/PageTitleContext';
import { useSidebar } from '../context/SidebarContext';
import { formatDateDDMMYYYY } from '../utils/dateUtils';
import usePageState from '../hooks/usePageState';
import { useAuth } from '../context/AuthContext';

const TagManager = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { getPagePermissions } = useAuth();
  const permissions = getPagePermissions('/admin/tags');
  const { setPageTitle } = usePageTitle();
  const { isCollapsed } = useSidebar();
  const [tags, setTags] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 30
  });

  const [filters, setFilters] = usePageState('tagManager', {
    currentPage: 1,
    itemsPerPage: 30,
    searchTerm: '',
    categoryFilter: '',
    startDate: '',
    endDate: '',
  }, ['/admin/tags']);
  const { currentPage, itemsPerPage, searchTerm, categoryFilter, startDate, endDate } = filters;
  const setCurrentPage    = (v) => setFilters({ currentPage: v });
  const setItemsPerPage   = (v) => setFilters({ itemsPerPage: v });
  const setSearchTerm     = (v) => setFilters({ searchTerm: v });
  const setCategoryFilter = (v) => setFilters({ categoryFilter: v });
  const setStartDate      = (v) => setFilters({ startDate: v });
  const setEndDate        = (v) => setFilters({ endDate: v });

  useEffect(() => {
    setPageTitle('Tags');
    fetchCategories();
  }, [setPageTitle]);

  useEffect(() => {
    fetchTags();
  }, [currentPage, searchTerm, categoryFilter, startDate, endDate, itemsPerPage]);

  const fetchCategories = async () => {
    try {
      const catsRes = await categoryService.getAll({ dropdown: true });
      // For dropdown, the response is the array directly
      setCategories(Array.isArray(catsRes.data) ? catsRes.data : []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
      setCategories([]);
    }
  };

  const fetchTags = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, limit: itemsPerPage };
      if (searchTerm) params.search = searchTerm;
      if (categoryFilter) params.category_code = categoryFilter;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const tagsRes = await tagService.getAll(params);
      setTags(tagsRes.data.data);
      setPagination(tagsRes.data.pagination);
    } catch (err) {
      setError('Failed to fetch tags');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    await Promise.all([fetchTags(), fetchCategories()]);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleCategoryFilterChange = (value) => {
    setCategoryFilter(value);
    setCurrentPage(1);
  };

  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
    setCurrentPage(1);
  };

  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setCategoryFilter('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleStatusToggle = async (tag) => {
    try {
      await tagService.updateStatus(tag.id, !tag.status);
      toast.success(`Tag ${!tag.status ? 'activated' : 'deactivated'} successfully`);
      fetchTags();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update tag status');
      console.error(err);
    }
  };

  const handleView = (tag) => {
    navigate(`/admin/tags/view/${tag.id}`);
  };

  const handleEdit = (tag) => {
    navigate(`/admin/tags/edit/${tag.id}`);
  };

  const handleDelete = async (tag) => {
    const confirmed = await toast.confirm('Are you sure you want to delete this tag?');
    if (confirmed) {
      try {
        await tagService.delete(tag.id);
        toast.success('Tag deleted successfully');
        fetchTags();
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to delete tag');
        console.error(err);
      }
    }
  };

  const handleCreate = () => {
    navigate('/admin/tags/create');
  };

  return (
    <div className="admin-panel h-full">
      <Card compact={isCollapsed} allowOverflow={true} noBottomPadding={true} className="h-full flex flex-col">
        <PageHeader 
          addButton={permissions.can_add ? {
            label: 'Add Tag',
            onClick: handleCreate,
            variant: 'primary'
          } : null}
          filters={{
            search: {
              value: searchTerm,
              onChange: handleSearchChange,
              placeholder: 'Search tags...'
            },
            // dateRange: {
            //   startDate: startDate,
            //   endDate: endDate,
            //   onStartDateChange: handleStartDateChange,
            //   onEndDateChange: handleEndDateChange
            // },
            custom: (
              <Select
                value={categoryFilter}
                onChange={(e) => handleCategoryFilterChange(e.target.value)}
                options={[
                  { value: '', label: 'All Categories' },
                  ...categories.map(cat => ({ value: cat.code, label: cat.name }))
                ]}
                placeholder="All Categories"
                className="w-[160px]"
              />
            )
          }}
        />

        {error && (
          <div className="p-4 mb-4 bg-danger-50 text-danger-700 rounded border-l-4 border-danger-600">
            {error}
          </div>
        )}

        <Table
          columns={[
            { 
              header: 'S.No', 
              accessor: 'serial',
              width: '80px',
              render: (_, row, index) => (currentPage - 1) * pagination.itemsPerPage + index + 1
            },
            { 
              header: 'Tag Name', 
              accessor: 'name',
              width: '200px',
              render: (value) => (
                <span className="text-sm font-medium text-gray-900">{value}</span>
              )
            },
            { 
              header: 'Description', 
              accessor: 'description',
              render: (value) =>
              //    (
              //   <span className="text-sm text-gray-600">
              //     {value || <span className="text-gray-400 italic">No description</span>}</span>
              // )
              value ? (
                <span className="text-sm text-gray-700" title={value}>
                  {value.length > 25 ? `${value.substring(0, 25)}...` : value}
                </span>
              ) : (
                <span className="text-sm text-gray-400 italic">No description</span>
              )
            },
            { 
              header: 'Category', 
              accessor: 'category_name',
              width: '180px',
              render: (value) => (
                <span className="text-sm text-gray-700">{value || 'N/A'}</span>
              )
            },
            {
              header: 'Created',
              accessor: 'created_at',
              width: '120px',
              render: (value) => formatDateDDMMYYYY(value),
            },
            {
              header: 'Status',
              accessor: 'status',
              width: '100px',
              align: 'center',
              render: (value, row) => (
                <StatusToggle status={value} onChange={() => handleStatusToggle(row)} disabled={!permissions.can_edit} />
              ),
            },
            {
              header: 'Actions',
              accessor: 'actions',
              width: '100px',
              align: 'center',
              actions: [
                ...(permissions.can_edit ? [{ type: 'edit', onClick: handleEdit }] : []),
                ...(permissions.can_delete ? [{ type: 'delete', onClick: handleDelete }] : [])
              ]
            },
          ]}
          data={permissions.can_list ? tags : []}
          loading={loading}
          onRowClick={permissions.can_view ? handleView : undefined}
          emptyMessage={!permissions.can_list ? 'Permission Denied - You don\'t have permission to view this data' : 'No tags found'}
        />
        
        <Pagination
          currentPage={currentPage}
          totalPages={pagination.totalPages}
          totalItems={pagination.totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      </Card>
    </div>
  );
};

export default TagManager;
